import React, { useState } from "react";
import styled from "styled-components";
import { FlexContainer, Spacing } from "../Containers";
import { ColorClassNames, ColorVariables } from "../Colors";
import { baseLength, getMobileBreakpoint, globalCSSVars } from "../Styles/globals";
import Checkbox from "../Inputs/Checkbox";

export type LegalDisclaimerProps = {
  error?: boolean;
  content: JSX.Element;
  includeCheckbox?: boolean;
  setIsDisclaimerChecked?: (val: boolean) => any;
  scrollable?: boolean;
  checkboxTitle?: JSX.Element;
};

export type LegalDisclaimerPropsWithoutContent = Omit<LegalDisclaimerProps, "content">;
export type LegalDisclaimerPropsWithoutContentHasCheckbox = LegalDisclaimerPropsWithoutContent & {
  setIsDisclaimerChecked: NonNullable<LegalDisclaimerProps["setIsDisclaimerChecked"]>;
};

const FormsContainer = styled.div`
  width: 100%;
  text-align: justify;
  a {
    text-decoration: none;
    font-weight: 300;
    &:hover {
      opacity: 0.8;
    }
  }

  @media (max-width: ${getMobileBreakpoint}) {
    width: ${globalCSSVars.mobileWidth};
  }
`;

const CustomScrollbar = styled(FlexContainer)`
  max-height: 90px;
  overflow-x: hidden;
  overflow-y: scroll;
  padding-right: 10px;
  margin-bottom: 5px;

  @media (min-width: 769px) {
    max-height: 120px;
  }

  &:hover {
    cursor: pointer;
  }
  /* width */
  ::-webkit-scrollbar {
    width: 5px;
    max-height: 30px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${ColorVariables.sugar};
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${ColorVariables.coco5};
    border-radius: 10px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: ${ColorVariables.coco7};
  }
`;

const DisclaimerContainer = ({ content }) => (
  <FlexContainer
    backgroundColor={ColorVariables.sugar}
    border={`1px solid ${ColorVariables.coco1}`}
    borderRadius={8}
    padding="10px 10px 0px 20px"
  >
    <CustomScrollbar>
      <p className="xsmall">{content}</p>
    </CustomScrollbar>
  </FlexContainer>
);

const LegalDisclaimer = ({
  error = false,
  content,
  includeCheckbox = false,
  setIsDisclaimerChecked,
  scrollable = false,
  checkboxTitle,
}: LegalDisclaimerProps) => {
  const [checked, setChecked] = useState(false);
  const checkHandler = () => {
    setChecked(!checked);
    if (setIsDisclaimerChecked) {
      setIsDisclaimerChecked(!checked);
    }
  };

  if (scrollable) {
    return (
      <>
        <DisclaimerContainer content={content} />
        <Spacing space={20} />
        {includeCheckbox && (
          <button data-name="checkHandler" onClick={checkHandler}>
            <FlexContainer>
              <Checkbox name="agree" error={error} checked={checked} />
              <Spacing mobileSpace={baseLength * 2.5} row space={baseLength * 2.5} />
              <span className={`xsmall ${ColorClassNames.coco5}`}>{checkboxTitle}</span>
            </FlexContainer>
          </button>
        )}
      </>
    );
  }

  return (
    <FlexContainer alignItems="center" className="disclaimer" justifyContent="center">
      <FormsContainer>
        <FlexContainer alignItems="flex-start">
          {includeCheckbox && (
            <>
              <Checkbox name="agree" error={error} checked={checked} onClick={checkHandler} />
              <Spacing mobileSpace={baseLength * 2.5} row space={baseLength * 2.5} />
            </>
          )}
          <div className={`xsmall ${ColorClassNames.coco5}`}>{content}</div>
        </FlexContainer>
      </FormsContainer>
    </FlexContainer>
  );
};

export { LegalDisclaimer };
